import type { Id } from "@convex/_generated/dataModel";
/* vortex-allow-color-file: thermal receipt/ticket print output — rendered on physical paper, literal ink colors required. */
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";

import {
  type LocationById,
  locationReceiptSettingsUpdateCustomerBillSettings,
} from "~/api-refs";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Switch } from "../ui/switch";

export interface CustomerBillReceiptSettings {
  showOrderName: boolean;
  showServerName: boolean;
  showSeatNumbers: boolean;
  printerId: string | null;
  customText: string | null;
  showModifiersBreakdown: boolean;
  showLocationNameAndLogo: boolean;
}

export function CustomerBillReceiptSettingsForm({
  settings,
  location,
}: {
  settings: CustomerBillReceiptSettings | null;
  location: LocationById;
}) {
  const updateSettingsMutation = useMutation({
    mutationFn: (data: {
      locationId: Id<"locations">;
      value: {
        showOrderName: boolean;
        showServerName: boolean;
        showSeatNumbers: boolean;
        printerId: Id<"printers"> | undefined;
        customText?: string;
        showModifiersBreakdown: boolean;
        showLocationNameAndLogo: boolean;
      };
    }) =>
      convexClient.mutation(
        locationReceiptSettingsUpdateCustomerBillSettings,
        data
      ),
    onSuccess: () => {
      ProductEvents.settingsUpdated("customer-bill");
    },
  });

  const form = useForm<CustomerBillReceiptSettings>({
    defaultValues: settings ?? {
      showOrderName: true,
      showServerName: true,
      showSeatNumbers: true,
      printerId: null,
      customText: null,
      showModifiersBreakdown: true,
      showLocationNameAndLogo: true,
    },
  });

  const [
    showSeatNumbers,
    showOrderName,
    showServerName,
    showModifiersBreakdown,
    showLocationNameAndLogo,
    customText,
  ] = form.watch([
    "showSeatNumbers",
    "showOrderName",
    "showServerName",
    "showModifiersBreakdown",
    "showLocationNameAndLogo",
    "customText",
  ]);

  const update = useDebouncedCallback(() => {
    const formValues = form.getValues();
    updateSettingsMutation.mutate({
      locationId: location._id,
      value: {
        ...formValues,
        printerId: formValues.printerId
          ? toConvexTableId("printers", formValues.printerId)
          : undefined,
        customText: formValues.customText ?? undefined,
      },
    });
  }, 1000);

  return (
    <div className="flex items-start gap-6">
      <div className="bg-muted rounded-lg p-6">
        <div className="caption bg-background text-foreground shadow-ambient w-56 rounded-md px-3.5 py-7 font-mono">
          <div className="text-center">
            Order: B1
            <br />
            Dine In
            {showServerName && (
              <>
                <br />
                Server: John
              </>
            )}
            <br />
            05/01/2024 4:20 PM
            {showOrderName && (
              <>
                <br />
                Table 3
              </>
            )}
          </div>
          <hr className="my-3.5 h-px border-none bg-black" />
          <div className="whitespace-pre">{`${showSeatNumbers ? "Seat 1\n" : ""}2 Cheeseburger X $8.00    $16.00
${
  showModifiersBreakdown
    ? `1 Chicken Burger           $8.00
  2 Bacon X $0.50          $1.00
  1 Tomato                 $0.00`
    : "1 Chicken Burger           $9.00"
}
${
  showModifiersBreakdown
    ? `1 Houseburger             $14.00
  - Double Patty           $4.00
  - Ketchup
    1 Regular              $0.00
    1 Spicy                $0.50
  - Fries
    - Cajun Spice          $2.00`
    : "1 Houseburger             $20.50"
}${showSeatNumbers ? "\nSeat 2" : ""}
1 Caesar Salad             $6.00
${
  showModifiersBreakdown
    ? `1 Latte                    $3.00
  - Regular                $0.00
  1 Vanilla                $1.00`
    : "1 Latte                    $4.00"
}
Subtotal                  $55.50`}</div>
          <hr className="my-3.5 h-px border-none bg-black" />
          <div className="whitespace-pre">{`Discount: Reward 10%      -$5.55
Discount: Loyalty Club    -$5.00
Tuesdays $5
Tax: Prepared Food 10%     $5.15
Tax: Beverages 8%          $0.32`}</div>
          <hr className="my-3.5 h-px border-none bg-black" />
          <div className="font-bold whitespace-pre">Bill Total: $50.42</div>
          {(showLocationNameAndLogo || !!customText) && (
            <>
              <hr className="my-3.5 h-px border-none bg-black" />
              <div className="flex flex-col items-center justify-center gap-2">
                {showLocationNameAndLogo && (
                  <>
                    <p>{location.name.slice(0, 32)}</p>
                    <img
                      alt={`${location.name} logo`}
                      className="size-10 rounded-full saturate-0"
                      height={40}
                      src={
                        location.logoUrl ??
                        "/images/location-logo-placeholder.svg"
                      }
                      width={40}
                    />
                  </>
                )}
                {customText && (
                  <p className="max-w-[32ch] text-center break-all">
                    {customText}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="w-full flex-1">
        <Form {...form}>
          <form className="space-y-4">
            <div>
              <h3 className="subtitle-sm text-foreground py-5 text-balance">
                Receipt Information
              </h3>
              <hr className="bg-accent mb-4 h-px border-none" />
            </div>
            <FormField
              control={form.control}
              name="showServerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show server name
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="showOrderName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show order name
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="showSeatNumbers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show seat numbers
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="showModifiersBreakdown"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    Show modifiers breakdown
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div>
              <h3 className="subtitle-sm text-foreground py-5 text-balance">
                Footer Branding
              </h3>
              <hr className="bg-accent mb-4 h-px border-none" />
            </div>
            <FormField
              control={form.control}
              name="showLocationNameAndLogo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          update();
                        }}
                      />
                    </FormControl>
                    <div className="font-medium">
                      Show location name and logo
                      <p className="body-sm text-muted-foreground">
                        Edit location details in{" "}
                        <a className="underline" href="../profile">
                          Profile
                        </a>{" "}
                        settings. Printed logos are converted to black and
                        white.
                      </p>
                    </div>
                  </FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="customText"
              render={({ field }) => (
                <>
                  <FormItem>
                    <FormLabel className="label text-foreground flex flex-row items-center gap-2">
                      <FormControl>
                        <Switch
                          checked={field.value !== null}
                          onCheckedChange={(v) => {
                            field.onChange(v ? "" : null);
                            update();
                          }}
                        />
                      </FormControl>
                      Show custom text
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                  {field.value !== null && (
                    <FormItem>
                      <FormLabel>Custom footer text</FormLabel>
                      <FormControl>
                        <textarea
                          className="border-border bg-background text-foreground field-sizing-content w-full max-w-2xl rounded-md border px-3 py-2"
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            update();
                          }}
                          value={field.value}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                </>
              )}
            />
          </form>
        </Form>
      </div>
    </div>
  );
}
